// Frame clock.
//
// There is no hit-stop, no impact freeze, no level-up slow-motion and no easing
// between timescales. There used to be a `hitStop(seconds)` here and a lerped
// `setScale`, and neither was ever called from gameplay — but leaving the
// machinery in place is an invitation, and this game must never slow down.
//
// An FPS that stutters every time you kill something is ruined by it. The kill
// is the moment the player is most in control; taking control away to celebrate
// it is exactly backwards. Ten kills in a row must feel the same as one.
//
// `scale` is therefore binary and instant: 1 while playing, 0 while paused. If
// you are here to add a punchy little freeze on crits, don't. Sell the impact
// with things that cost no time — a brighter flash, a louder transient, more
// debris, a harder camera kick that resolves inside the same frame budget.
export class Time {
  constructor() {
    this.now = performance.now() / 1000;
    this.dt = 0;          // scaled delta (what gameplay uses)
    this.rawDt = 0;       // unscaled delta (UI, camera smoothing)
    this.elapsed = 0;     // scaled accumulated time
    this.rawElapsed = 0;
    this.scale = 1;
    this.frame = 0;
    this.fps = 60;
    this._fpsAccum = 0;
    this._fpsFrames = 0;
  }

  /**
   * Running or paused. Anything other than 0 is treated as 1 — there is no
   * fractional timescale, so no caller can introduce a slowdown by passing one.
   * The second argument is accepted and ignored so the old lerp call sites
   * cannot silently change meaning.
   */
  setScale(target) {
    this.scale = target ? 1 : 0;
  }

  tick() {
    const t = performance.now() / 1000;
    let raw = t - this.now;
    this.now = t;
    // Clamp to avoid physics explosions after tab-out / breakpoint.
    if (raw > 0.1) raw = 0.1;
    this.rawDt = raw;
    this.rawElapsed += raw;

    this.dt = raw * this.scale;
    this.elapsed += this.dt;
    this.frame++;

    this._fpsAccum += raw;
    this._fpsFrames++;
    if (this._fpsAccum >= 0.25) {
      this.fps = this._fpsFrames / this._fpsAccum;
      this._fpsAccum = 0;
      this._fpsFrames = 0;
    }
  }
}
